import {
  ALLOCATION_STATUS_LABELS,
  LEAVE_STATUS_LABELS,
  LEAVE_TYPE_LABELS,
  RESERVATION_TYPE_LABELS,
} from "../resources/resourceVocabulary";
import {
  REOPEN_STATUS_LABELS,
  TIMESHEET_STATUS_LABELS,
} from "../timesheets/timesheetVocabulary";
import {
  APPROVAL_LABELS,
  CHANGE_TYPE_LABELS,
  SCOPE_KIND_LABELS,
} from "../change/changeVocabulary";
import { PROJECT_ROLE_LABELS } from "../projects/projectVocabulary";
import { optionsFrom, type GuideChapter } from "./guideTypes";

/** People, time, and the settings behind both. */
export const ORGANISATION_CHAPTER: GuideChapter = {
  id: "organisation",
  title: "People and time",
  sections: [
    {
      id: "teams",
      title: "Teams",
      where: "Teams in the left menu",
      purpose:
        "Who works together. A person's home team is where they sit; a delivery team is what "
        + "they are doing now. Membership is dated, so the answer to \"who was on this in March\" "
        + "is still there in August.",
      examples: [
        {
          title: "Moving somebody between teams",
          steps: [
            "Open the team and use Move member rather than removing and re-adding.",
            "The old membership is closed with an end date and a new one opens. Their history "
            + "stays intact, and reports covering March still attribute March's work correctly.",
          ],
        },
      ],
      tables: [
        {
          title: "Project roles",
          intro: "What somebody does on a particular project, which can differ from their job.",
          options: optionsFrom(PROJECT_ROLE_LABELS, {
            PROJECT_MANAGER: "Accountable for delivery.",
            TEAM_LEAD: "Runs the team day to day.",
            DEVELOPER: "Builds it.",
            QA: "Tests it.",
            DESIGNER: "Designs it.",
            DEVOPS: "Deploys and runs it.",
            BA: "Works out what is needed.",
            STAKEHOLDER: "Has an interest, does not do the work.",
          }),
        },
      ],
    },

    {
      id: "resources",
      title: "Resources",
      where: "Resources in the left menu",
      purpose:
        "Who is available, and for what. Allocations say how much of somebody's week a project "
        + "has; reservations account for the time that is not project work at all.",
      examples: [
        {
          title: "Finding out why somebody is over-allocated",
          steps: [
            "The heatmap shows anyone above 100%. Open the week.",
            "Usually it is two projects each assuming 60%, or an allocation nobody ended when a "
            + "project finished.",
            "Reservations — support rota, training — are counted too, because a developer on the "
            + "support rota is genuinely not available for a sprint.",
          ],
        },
      ],
      tables: [
        {
          title: "Allocation status",
          options: optionsFrom(ALLOCATION_STATUS_LABELS, {
            PLANNED: "Intended. Counts towards forecasts, not commitments.",
            CONFIRMED: "Agreed. Counts as real capacity.",
            COMPLETED: "Finished.",
            CANCELLED: "Called off before it started.",
          }),
        },
        {
          title: "Reservation types",
          intro: "Time that is not available for project work.",
          options: optionsFrom(RESERVATION_TYPE_LABELS, {
            TRAINING: "Courses, certification, learning time.",
            INTERNAL_MEETINGS: "Standing meetings that are not a project's.",
            ADMIN: "Administration, appraisals, recruitment.",
            SUPPORT_ROTA: "On call or on the support desk.",
            OTHER: "Anything else, with a note.",
          }),
        },
      ],
    },

    {
      id: "leave",
      title: "Leave",
      where: "Leave in the left menu",
      purpose:
        "Absence, and its effect on capacity. Approved leave reduces what a sprint can plan for "
        + "— a team of five with two people away is not a team of five.",
      examples: [
        {
          title: "Booking a week off",
          steps: [
            "Request annual leave for the dates. It is Requested.",
            "Your manager approves it. Sprint capacity for those days drops automatically.",
            "Cancelling approved leave puts the capacity back.",
          ],
        },
      ],
      tables: [
        {
          title: "Leave type",
          options: optionsFrom(LEAVE_TYPE_LABELS, {
            ANNUAL: "Holiday.",
            SICK: "Illness.",
            UNPAID: "Agreed time off without pay.",
            PARENTAL: "Maternity, paternity, adoption.",
            COMPASSIONATE: "Bereavement and family emergencies.",
            OTHER: "Anything else, with a note.",
          }),
        },
        {
          title: "Leave status",
          options: optionsFrom(LEAVE_STATUS_LABELS, {
            REQUESTED: "Asked for, not yet answered. Does not affect capacity.",
            APPROVED: "Granted. Capacity is reduced.",
            REJECTED: "Refused.",
            CANCELLED: "Withdrawn. Capacity returns.",
          }),
        },
      ],
    },

    {
      id: "timesheets",
      title: "Timesheets",
      where: "Timesheets, and Timesheet approvals for managers",
      purpose:
        "Where the hours went. Booked against work items, so a project's actual effort is the "
        + "sum of real work rather than a number somebody estimated afterwards.",
      examples: [
        {
          title: "A week, end to end",
          steps: [
            "Book time against items as you go. The sheet is a Draft and yours to change.",
            "On Friday, submit it. It becomes Waiting for approval and you can no longer edit it.",
            "Your manager approves it, or sends it back with a comment — in which case it is "
            + "yours again.",
            "Approved sheets lock at period close. If something was genuinely wrong, request a "
            + "reopen: that is a decision somebody makes, recorded, rather than a quiet edit to a "
            + "settled figure.",
          ],
        },
      ],
      tables: [
        {
          title: "Timesheet status",
          options: optionsFrom(TIMESHEET_STATUS_LABELS, {
            DRAFT: "Yours. Editable.",
            SUBMITTED: "With your manager. Not editable.",
            APPROVED: "Accepted. Counts as actual effort.",
            REJECTED: "Sent back with a comment. Yours again.",
            LOCKED: "The period is closed. Only a reopen can change it.",
          }),
        },
        {
          title: "Reopen request",
          options: optionsFrom(REOPEN_STATUS_LABELS, {
            PENDING: "Asked for, waiting on a decision.",
            APPROVED: "Granted — the sheet is editable again.",
            REJECTED: "Refused. The figure stands.",
          }),
        },
      ],
    },

    {
      id: "schedule-changes",
      title: "Schedule changes",
      where: "Schedule changes in the left menu",
      purpose:
        "Every time a date moved, and why. A project that slipped four weeks did so in a series "
        + "of small steps, each of which seemed reasonable — this is the list that makes the "
        + "pattern visible.",
      examples: [
        {
          title: "Moving a committed date",
          steps: [
            "Change the committed due date on an item. You are asked why.",
            "Depending on the project's rules that may need sign-off before it takes effect.",
            "The entry stays on the record whether or not it was approved — an append-only "
            + "ledger, so a slip cannot be tidied away later.",
          ],
        },
      ],
      tables: [
        {
          title: "What moved",
          options: optionsFrom(CHANGE_TYPE_LABELS, {
            COMMITTED_START: "The date promised for starting.",
            COMMITTED_DUE: "The date promised for finishing. The one that matters most.",
            FORECAST_START: "The current expectation of starting.",
            FORECAST_DUE: "The current expectation of finishing.",
          }),
        },
        {
          title: "Sign-off",
          options: optionsFrom(APPROVAL_LABELS, {
            NOT_REQUIRED: "Small enough not to need one.",
            PENDING: "Waiting for a decision.",
            APPROVED: "Agreed.",
            REJECTED: "Declined — the original date stands.",
          }),
        },
        {
          title: "Scope changes",
          intro: "On a project, measured against the signed-off baseline.",
          options: optionsFrom(SCOPE_KIND_LABELS, {
            ADDED: "Work that was not in the baseline.",
            REMOVED: "Work that was and is not any more.",
            ESTIMATE_CHANGED: "Same work, different size.",
          }),
        },
      ],
    },

    {
      id: "reports",
      title: "Reports",
      where: "Reports in the left menu",
      purpose:
        "Delivery and workforce figures across projects. Every report is bounded by a window, "
        + "because \"how are we doing?\" is always a question about a period.",
      examples: [
        {
          title: "Preparing for a delivery review",
          steps: [
            "Pick the window — last quarter, since the new process started.",
            "Throughput says how much finished. Estimate accuracy says whether the team's sizing "
            + "can be trusted.",
            "Missed deadlines names the items, not just a count, so the conversation is about "
            + "specific work rather than a percentage.",
          ],
        },
      ],
      tables: [],
    },

    {
      id: "administration",
      title: "Administration",
      where: "The Administration group in the left menu",
      purpose:
        "Settings most people never open. Changes here reach across every project, which is why "
        + "they sit behind their own permissions.",
      examples: [
        {
          title: "Adding a second production environment",
          steps: [
            "Environments, then New environment.",
            "Code PROD_EU, name \"Production (EU)\", and tick Treat this as production.",
            "That tick — not the name — is what makes releases to it governed as production. A "
            + "customer with two regions gets the same treatment for both.",
          ],
        },
        {
          title: "Asking a new question of every release",
          steps: [
            "Define a release field: key cab_reference, label \"CAB reference\", type Text, "
            + "required.",
            "Every release now shows it under Additional details, and an unanswered one fails the "
            + "Required fields gate by name.",
            "Scope it to a release type if it only applies to some — an incident reference that "
            + "only hotfixes need, for instance.",
          ],
        },
      ],
      tables: [
        {
          title: "What each screen does",
          options: [
            {
              name: "Employees",
              meaning:
                "People, their manager, their working calendar and their roles. Deactivating "
                + "somebody keeps their history.",
            },
            {
              name: "Roles & Permissions",
              meaning:
                "What each role may do. Permissions are checked on the server, so hiding a button "
                + "is a courtesy rather than the control.",
            },
            {
              name: "Release Templates",
              meaning: "The governance each kind of release is held to. Versioned.",
            },
            {
              name: "Environments",
              meaning:
                "Where releases go. Withdraw rather than delete — deployment history points at "
                + "them.",
            },
            {
              name: "Calendars & Holidays",
              meaning:
                "Working days and hours per calendar. Capacity, due dates and burndown all count "
                + "in working time, so a public holiday is not a day the plan expects work.",
            },
          ],
        },
        {
          title: "Custom field types",
          intro: "For release fields. The type decides how a value is checked when it is saved.",
          options: [
            { name: "Text", meaning: "One line. A reference number, a name." },
            { name: "Text area", meaning: "Several lines — a justification, a note." },
            {
              name: "Number",
              meaning:
                "Checked as a number, so \"about 40\" is refused rather than stored and "
                + "discovered later by a report trying to add it up.",
            },
            { name: "Date", meaning: "Checked as a real date." },
            { name: "Yes / no", meaning: "A tick." },
            { name: "List", meaning: "One of a fixed set of options you supply." },
            { name: "Link", meaning: "Checked as a full URL, so a half-typed one is caught." },
          ],
        },
      ],
      notes: [
        "Nothing here is deleted. Environments and fields are withdrawn, employees deactivated, "
        + "templates retired — because completed work points at all of them, and an audit asks "
        + "what things were at the time.",
      ],
    },
  ],
};
